import React, { useState } from 'react';
import { Upload, FileText, X, AlertCircle, CheckCircle, LoaderIcon } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { submitForm } from '../utils/submitForm';
const MAX_FILE_SIZE = 48 * 1024 * 1024; // 48 MB
export function ContactSection() {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  // Form state
  const [company, setCompany] = useState('');
  const [person, setPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [task, setTask] = useState('');
  const [project, setProject] = useState('');
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const validateFile = (file: File): boolean => {
    setFileError('');
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds 48 MB limit');
      return false;
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Invalid file type. Please upload PDF, DOC, DOCX, XLS, XLSX, or image files');
      return false;
    }
    return true;
  };
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };
  const removeFile = () => {
    setUploadedFile(null);
    setFileError('');
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const resetForm = () => {
    setCompany('');
    setPerson('');
    setEmail('');
    setPhone('');
    setTask('');
    setProject('');
    setUploadedFile(null);
    setFileError('');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitStatus('idle');
    // Build message
    const messageParts: string[] = [];
    if (person) messageParts.push(`Contact: ${person}`);
    if (task) messageParts.push(`Task: ${task}`);
    if (project) messageParts.push(`Project/Vessel: ${project}`);
    const fd = new FormData();
    fd.append('name', company);
    fd.append('email', email);
    fd.append('phone', phone);
    fd.append('message', messageParts.join(' | '));
    if (uploadedFile) fd.append('file', uploadedFile);
    try {
      const result = await submitForm('home', fd, language);
      if (result.ok) {
        setSubmitStatus('success');
        resetForm();
        setTimeout(() => setSubmitStatus('idle'), 4000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 6000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 6000);
    } finally {
      setSubmitting(false);
    }
  };
  return <section className="w-full py-16 px-6 md:px-12 bg-[var(--color-bg)]" data-id="element-176">
      <div className="max-w-3xl mx-auto" data-id="element-177">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8" data-id="element-178">
          <div className="text-center mb-8" data-id="element-179">
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-2" data-id="element-180">
              {t('contact.title')}
            </h2>
            <p className="text-[var(--color-text-light)] text-sm" data-id="element-181">
              {t('contact.desc')}
            </p>
          </div>

          {/* Success message */}
          {submitStatus === 'success' && <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-id="element-182">
              <CheckCircle className="w-5 h-5 flex-shrink-0" data-id="element-183" />
              <span className="font-medium" data-id="element-184">{t('formStatus.sent')}</span>
            </div>}

          {/* Error message */}
          {submitStatus === 'error' && <div className="mb-6 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-185">
              <AlertCircle className="w-5 h-5 flex-shrink-0" data-id="element-186" />
              <span data-id="element-187">{t('formStatus.error')}</span>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-188">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-189">
              <div data-id="element-190">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-191">
                  {t('contact.labels.company')}{' '}
                  <span className="text-red-500" data-id="element-192">*</span>
                </label>
                <input type="text" required value={company} onChange={e => setCompany(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-193" />
              </div>
              <div data-id="element-194">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-195">
                  {t('contact.labels.person')}{' '}
                  <span className="text-red-500" data-id="element-196">*</span>
                </label>
                <input type="text" required value={person} onChange={e => setPerson(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-197" />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-198">
              <div data-id="element-199">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-200">
                  {t('contact.labels.email')}{' '}
                  <span className="text-red-500" data-id="element-201">*</span>
                </label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-202" />
              </div>
              <div data-id="element-203">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-204">
                  {t('contact.labels.phone')}{' '}
                  <span className="text-red-500" data-id="element-205">*</span>
                </label>
                <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors placeholder:text-gray-400" data-id="element-206" />
                <p className="text-xs text-[var(--color-text-light)] mt-1" data-id="element-207">
                  {t('contact.labels.phoneHint')}
                </p>
              </div>
            </div>

            {/* Textarea */}
            <div data-id="element-208">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-209">
                {t('contact.labels.task')}{' '}
                <span className="text-red-500" data-id="element-210">*</span>
              </label>
              <textarea rows={4} required value={task} onChange={e => setTask(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors resize-none placeholder:text-gray-400" data-id="element-211" />
            </div>

            {/* Project type */}
            <div data-id="element-212">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-213">
                {t('contact.labels.project')}
              </label>
              <input type="text" value={project} onChange={e => setProject(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-214" />
            </div>

            {/* File upload */}
            <div data-id="element-215">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-216">
                {t('contact.labels.files')}
              </label>

              {!uploadedFile ? <div data-id="element-217">
                  <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => document.getElementById('contact-file-upload')?.click()} className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragActive ? 'border-[var(--color-tan)] bg-[var(--color-tan)]/5' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-218">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-219" />
                    <p className="text-sm text-[var(--color-text-dark)] mb-1" data-id="element-220">
                      {t('contact.labels.dragDrop')}
                    </p>
                    <p className="text-xs text-[var(--color-text-light)]" data-id="element-221">
                      {t('contact.labels.fileTypes')}
                    </p>
                    <input id="contact-file-upload" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleFileChange} className="hidden" data-id="element-222" />
                  </div>
                  {fileError && <div className="mt-2 flex items-start gap-2 text-red-500 text-sm" data-id="element-223">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-224" />
                      <span data-id="element-225">{fileError}</span>
                    </div>}
                </div> : <div className="border-2 border-[var(--color-tan)] bg-[var(--color-tan)]/5 rounded-lg p-4 flex items-center justify-between" data-id="element-226">
                  <div className="flex items-center gap-3" data-id="element-227">
                    <FileText className="w-8 h-8 text-[var(--color-tan)]" data-id="element-228" />
                    <div data-id="element-229">
                      <p className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-230">
                        {uploadedFile.name}
                      </p>
                      <p className="text-xs text-[var(--color-text-light)]" data-id="element-231">
                        {formatFileSize(uploadedFile.size)}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={removeFile} className="p-2 hover:bg-red-50 rounded-full transition-colors" data-id="element-232">
                    <X className="w-5 h-5 text-red-500" data-id="element-233" />
                  </button>
                </div>}
            </div>

            {/* Submit button - Centered */}
            <div className="flex justify-center pt-2" data-id="element-234">
              <button type="submit" disabled={submitting} className={`px-8 py-3 text-white text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${submitting ? 'bg-gray-300 cursor-not-allowed' : 'bg-[var(--color-tan)] hover:opacity-90'}`} data-id="element-235">
                {submitting ? <>
                    <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-236" />
                    {t('formStatus.sending')}
                  </> : t('contact.button')}
              </button>
            </div>

            <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-237">
              {t('contact.disclaimer')}
            </p>
          </form>
        </div>
      </div>
    </section>;
}